import { useAtomValue } from 'jotai';
import { demoModeAtom, userDataAtom } from '@/global-store';
import { getGovernanceDomainAccessLevel } from '@/guards/governanceAccess';
import { useEffectiveUserRights } from '@/http/queries/useEffectiveUserRights';

/** See getGovernanceDomainAccessLevel for the access model. */
export function useGovernanceAccess(domainId?: number) {
  const demoMode = useAtomValue(demoModeAtom);
  const effectiveRights = useEffectiveUserRights();
  const user = useAtomValue(userDataAtom);
  const isPrivileged = demoMode || user?.isAdmin || user?.isSalesAdmin;
  const isLoading = !isPrivileged && effectiveRights === null;
  const accessLevel = getGovernanceDomainAccessLevel({
    domainId,
    demoMode,
    effectiveRights,
    user,
  });

  return {
    accessLevel,
    canEdit: accessLevel === 'editor',
    hasAccess: accessLevel !== null,
    isPrivileged,
    isLoading,
  };
}

/**
 * Predicate form for lists: decides access per Governance domain without
 * re-running the hooks for every row.
 */
export function useCanEditGovernanceDomain(): (domainId: number) => boolean {
  const demoMode = useAtomValue(demoModeAtom);
  const effectiveRights = useEffectiveUserRights();
  const user = useAtomValue(userDataAtom);

  return (domainId: number) =>
    getGovernanceDomainAccessLevel({
      domainId,
      demoMode,
      effectiveRights,
      user,
    }) === 'editor';
}
